import { useEffect, useState } from "react"
import _ from "lodash"
import PropTypes from "prop-types"
import { useLocation } from "react-router-dom"
import { useAuth } from "../../context/authContext"

// Helpers
import { clearSessStorage, getSessStorage } from "../../helpers/sessionStorage"

// Hooks
import useLogs from "../../hooks/useLogs" // Should be rm?

// assets
import logo from "../../assets/images/logo-white-text.png"
import AuthMessages from "./components/AuthMessages"
import LoginScreen from "./components/LoginScreen"
import ResetPasswordScreen from "./components/ResetPasswordScreen"
import SmsVerificationScreen from "./components/SmsVerificationScreen"
import TotpVerificationScreen from "./components/TotpVerificationScreen"

import { LogError } from "../../helpers/logger"
import { ValidateEmail } from "../../helpers/emailHelpers"
import { getAuthErrorMessage } from "../../helpers/authErrorMessage"
import { useRecaptcha } from "../../hooks/captcha/useRecaptcha"
import useLoginMfa from "./hooks/useLoginMfa"

const LOGIN_SCREEN = "LOGIN"
const RESET_PASSWORD_SCREEN = "RESET_PASSWORD"
const MFA_SMS_SCREEN = "MFA_SMS"
const MFA_TOTP_SCREEN = "MFA_TOTP"

export default function Login(props) {
  const location = useLocation()
  const {
    auth,
    errSession,
    setErrSession,
    signOut,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
  } = useAuth()
  const {
    verifier: recaptchaVerifier,
    error: recaptchaInitError,
    isReady: recaptchaReady,
  } =
    useRecaptcha(auth)

  const maintenance = false

  const [loading, setLoading] = useState(false)
  const [forgotPasswordScreen, setForgotPasswordScreen] = useState(false)
  const [failedAttempts, setFailedAttempts] = useState(0)

  const [logsEnable, writeLog] = useLogs() // Should be rm?

  const [user, setUser] = useState("")
  const [password, setPassword] = useState("")

  const [err, setErr] = useState(null)
  const [msg, setMsg] = useState(null)

  // Check if user has too many failed attempts
  useEffect(() => {
    if (failedAttempts >= 3) {
      setErr("Too many failed attempts. Please reset your password.")
      setForgotPasswordScreen(true)
    }
  }, [failedAttempts])

  // Check for Session Errors
  useEffect(() => {
    if (errSession !== "") {
      setErr(errSession)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errSession])

  useEffect(() => {
    const authActionError = getSessStorage("auth_action_error")
    if (!authActionError) return

    setErr(authActionError)
    setMsg(null)
    clearSessStorage("auth_action_error")
  }, [])

  useEffect(() => {
    const prefillEmail = _.trim(location.state?.prefillEmail || "")
    if (!prefillEmail) return

    setUser(prefillEmail)
  }, [location.state?.prefillEmail])

  const handleTotpChallengeTimeout = (
    message = "Your verification session expired. Please sign in again to continue.",
  ) => {
    setPassword("")
    setMsg(null)
    resetMfaState()
    setErr(message)
  }

  const {
    has2FA,
    resolver,
    twoFAMethod,
    verificationId,
    verificationSMSCode,
    setVerificationSMSCode,
    verificationTOTPCode,
    setVerificationTOTPCode,
    isResending,
    autoSmsNeedsManualRequest,
    requestSmsCode,
    handleMFARequiredError,
    resolveSMS,
    resolveTOTP,
    resetMfaState,
    resetMfaBeforeSignIn,
  } = useLoginMfa({
    auth,
    recaptchaVerifier,
    recaptchaReady,
    recaptchaInitError,
    userEmail: user,
    setErr,
    getAuthErrorMessage,
    onTotpChallengeTimeout: handleTotpChallengeTimeout,
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (loading) return

    // Reset any currently displayed messages
    setMsg(null)
    setErr(null)
    resetMfaBeforeSignIn()

    // Confirm that the e-mail is valid.
    const emailValidation = ValidateEmail(user)
    if (!emailValidation.isValid) {
      setErr(emailValidation.message)
      return
    }

    setLoading(true)
    try {
      // If a stale Firebase user is still cached from a failed bootstrap,
      // force a state transition so onAuthStateChanged runs again on retry.
      if (auth?.currentUser) {
        await signOut({ redirect: false })
      }

      setErrSession("")
      await signInWithEmailAndPassword(user, password)
    } catch (error) {
      LogError("+++++++++AUTH Error+++++++++: ", error.code, error)
      const authErrMessage = getAuthErrorMessage(error)

      // Add error handlers
      switch (error.code) {
        case "auth/user-not-found":
          setErr(authErrMessage)
          return
        case "auth/wrong-password":
          setErr(authErrMessage)
          setFailedAttempts((prevAttempts) => prevAttempts + 1)
          return
        case "auth/multi-factor-auth-required":
          await handleMFARequiredError({ multiFactorError: error })
          return
        case "auth/internal-error":
          setErr(authErrMessage)
          return
        default:
          setErr(authErrMessage)
          return
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitRecover = async (e) => {
    setLoading(true)
    e.preventDefault()
    const emailValidation = ValidateEmail(user)
    if (!emailValidation.isValid) {
      setLoading(false)
      setErr(emailValidation.message)
      setMsg(null)
      return
    }

    try {
      // Verify if the user requires MFA
      if (has2FA && resolver) {
        setMsg(
          "Please complete multi-factor authentication first (SMS or TOTP).",
        )
        setLoading(false)
        return
      }

      // Send the password reset email
      await sendPasswordResetEmail(user)
      if (logsEnable) {
        await writeLog({
          type: "info",
          component: "Auth",
          message: `User resets password: ${user}`,
          stack: "",
        })
      }

      const msgTxt =
        "If your email is registered, a password reset link has been sent. Check your inbox and spam folder."
      setErr(null)
      setMsg(msgTxt)
      setForgotPasswordScreen(false)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      setErr(error.message)
      setMsg()
    }
  }

  const logoutMsg = getSessStorage("logout")

  const handleResolveSMS = async () => {
    if (loading || isResending) return
    setLoading(true)

    try {
      await resolveSMS()
    } finally {
      setLoading(false)
    }
  }

  const handleResolveTOTP = async () => {
    if (loading || isResending) return
    setLoading(true)

    try {
      await resolveTOTP()
    } finally {
      setLoading(false)
    }
  }

  // Returning from MFA should clear sensitive/login transient state before showing login again.
  const handleBackToLoginFromMfa = () => {
    setPassword("")
    setErr(null)
    setMsg(null)
    resetMfaState()
  }

  // One active screen at a time keeps Login/Reset/MFA flows mutually exclusive.
  const currentScreen = (() => {
    if (twoFAMethod === "sms") return MFA_SMS_SCREEN
    if (twoFAMethod === "totp") return MFA_TOTP_SCREEN
    if (forgotPasswordScreen) return RESET_PASSWORD_SCREEN
    return LOGIN_SCREEN
  })()

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case LOGIN_SCREEN:
        return (
          <LoginScreen
            maintenanceInfo={props.maintenance}
            maintenanceDisabled={maintenance}
            loading={loading}
            user={user}
            password={password}
            onSubmit={handleSubmit}
            onUserChange={(value) => setUser(_.trim(value))}
            onPasswordChange={(value) => setPassword(value)}
            onForgotPassword={() => {
              setPassword("")
              setErr(null)
              setMsg(null)
              resetMfaState()
              setForgotPasswordScreen(true)
            }}
          />
        )
      case RESET_PASSWORD_SCREEN:
        return (
          <ResetPasswordScreen
            loading={loading}
            user={user}
            onSubmit={handleSubmitRecover}
            onUserChange={(value) => setUser(_.trim(value))}
            onBack={() => {
              setForgotPasswordScreen(false)
              setErr(null)
              setMsg(null)
              resetMfaState()
            }}
          />
        )
      case MFA_SMS_SCREEN:
        return (
          <SmsVerificationScreen
            resolver={resolver}
            verificationSMSCode={verificationSMSCode}
            onVerificationSMSCodeChange={setVerificationSMSCode}
            onResolveSMS={handleResolveSMS}
            isResending={isResending}
            autoSmsNeedsManualRequest={autoSmsNeedsManualRequest}
            loading={loading}
            verificationId={verificationId}
            recaptchaVerifier={recaptchaVerifier}
            recaptchaReady={recaptchaReady}
            onResend={requestSmsCode}
            onBack={handleBackToLoginFromMfa}
          />
        )
      case MFA_TOTP_SCREEN:
        return (
          <TotpVerificationScreen
            verificationTOTPCode={verificationTOTPCode}
            onVerificationTOTPCodeChange={setVerificationTOTPCode}
            onResolveTOTP={handleResolveTOTP}
            isResending={isResending}
            loading={loading}
            onBack={handleBackToLoginFromMfa}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="layout-login">
      <img src={logo} alt="Recovery Link Logo" className="main-logo" />
      <div className="login-container">
        {renderCurrentScreen()}
        <AuthMessages err={err} msg={msg} logoutMsg={logoutMsg} />
      </div>
    </div>
  )
}

Login.propTypes = {
  maintenance: PropTypes.object.isRequired,
}
